<template>
  <div>
    <base-table-layout>
      <template #popover>
        <el-popover
          ref="popover"
          v-model:visible="popoverVisible"
          trigger="click"
          :popper-class="popoverClass"
          :placement="popoverPlacement"
          width="200"
          :show-arrow="false"
          class="hidden-reference"
          :append-to-body="false"
        >
          <template #reference></template>
          <div>
            {{ customPopover || popoverContent }}
          </div>
        </el-popover>
        <!--        <el-popover-->
        <!--          ref="popover"-->
        <!--          v-model:visible="popoverVisible"-->
        <!--          :content="customPopover || popoverContent"-->
        <!--          :popper-class="popoverClass"-->
        <!--          placement="top"-->
        <!--          width="150"-->
        <!--          trigger="click"-->
        <!--        />-->
      </template>
      <template #table>
        <el-table
          ref="base-table"
          v-loading="loading"
          v-linker="{id: sharedId}"
          :data="paginateData"
          :max-height="appliedMaxHeight"
          :border="border"
          :fit="fit"
          class="content"
          :row-class-name="rowClassName"
          :cell-class-name="cellClassName"
          :cell-style="cellStyle"
          :size="size"
          :summary-method="summaryMethod"
          :show-summary="showSummary"
          :show-header="showHeader"
          :row-key="rowKey"
          :expand-row-keys="expandRowKeys"
          :highlight-current-row="highlightCurrentRow"
          :height="height"
          :style="{
            'height': minVH ? maxHeight : undefined
          }"
          @row-click="handleRowClick"
          @expand-change="handleExpandChange"
          @mouseleave="resetPopover"
          @selection-change="handleSelectionChange"
        >
          <slot/>
        </el-table>
      </template>
      <template v-if="page.size && !hiddenPagination" #pagination>
        <table-pagination
          :total-items="page.total"
          :items-per-page="page.size"
          :page="page.current"
          @update:items-per-page="val => page.size = val"
          @update:page="val => page.current = val"
        />
      </template>
    </base-table-layout>
  </div>
</template>
<script>
import WindowResize from "@/mixins/window-resize.js"
import BaseTableMixin from "@/components/base/table/base-table-mixin.js"

import BaseTableLayout from "@/components/base/table/base-table-layout.vue"
import TablePagination from "@/components/base/table/table-pagination.vue"

export default {
  name: "BaseTable",
  components: {
    BaseTableLayout,
    TablePagination,
  },
  mixins: [
    WindowResize,
    BaseTableMixin,
  ],
  props: {
    "items": {
      type: Array,
      default: () => [],
    },
    "loading": {
      type: Boolean,
      default: true,
    },
    "exclusiveHeight": {
      type: Number,
      default: null,
    },
    "minHeight": {
      type: Number,
      default: null,
    },
    "minVH": {
      type: Number,
      default: null
    },
    "pageSize": {
      type: Number,
      default: 20,
    },
    "pageCurrent": {
      type: Number,
      default: 0,
    },
    "border": {
      type: Boolean,
      default: true,
    },
    "fit": {
      type: Boolean,
      default: true,
    },
    "rowClassName": {
      type: Function,
      default: () => "",
    },
    "cellClassName": {
      type: Function,
      default: () => {},
    },
    cellStyle: {
      type: Function,
      default: () => {},
    },
    "handleSelectionChange": {
      type: Function,
      default: () => [],
    },
    "handleExpandChange": {
      type: Function,
      default: () => [],
    },
    "size": {
      type: String,
      default: "default",
    },
    "summaryMethod": {
      type: Function,
      default: () => {},
    },
    "spanMethod": {
      type: Function,
      default: () => {},
    },
    "showSummary": {
      type: Boolean,
      default: false,
    },
    "showHeader": {
      type: Boolean,
      default: true,
    },
    "rowKey": {
    },
    "expandRowKeys": {
    },
    highlightCurrentRow: {
      type: Boolean,
      default: false,
    },
    "sharedId": {
      type: [Number, String],
      default: null,
    },
    hiddenPagination: {
      type: Boolean,
      default: false
    },
    // Глобальная настройка: форматировать ли числа в ячейках по умолчанию
    formatNumbers: {
      type: Boolean,
      default: true,
    },
    height: {
      type: [String, Number],
      default: ''
    }
  },
  emits: ['update:pageCurrent'],
  provide() {
    return {
      baseTableFormatNumbers: this.formatNumbers,
    }
  },
  data() {
    return {
      headerResizeObserver: null,
      observedHeaderEl: null,
      layoutRaf: null,
      initialHeaderHeight: null,
      headerHeightDelta: 0,
    }
  },
  computed: {
    rawMaxHeight() {
      if (this.height) {
        return this.height
      }
      if (this.exclusiveHeight != null) {
        const viewportHeight = this.$store.getters["WindowResize/getHeight"]
        if (viewportHeight) {
          let maxHeight = viewportHeight - this.exclusiveHeight
          if (this.minHeight) {
            maxHeight = Math.max(maxHeight, this.minHeight)
          }
          return maxHeight
        }
        return this.minHeight || undefined
      }
      if (this.minVH) {
        return `${this.minVH}vh`
      }
      return this.minHeight ? this.minHeight : '100%'
    },
    appliedMaxHeight() {
      const raw = this.rawMaxHeight
      if (typeof raw !== "number") {
        return raw
      }
      if (!this.shouldAdjustMaxHeight) {
        return raw
      }
      const delta = this.headerHeightDelta || 0
      const adjusted = raw - delta
      if (this.minHeight && adjusted < this.minHeight) {
        return this.minHeight
      }
      return Math.max(adjusted, 0)
    },
    shouldAdjustMaxHeight() {
      return this.exclusiveHeight != null && !this.height
    },
    pageSizeComputed() {
      return this.hiddenPagination ? this.items.length : this.pageSize
    }
  },
  watch: {
    items: {
      handler: function (val) {
        const totalPageWithoutIsTotalRow = val.filter(item => !item?.isTotal)
        this.page.total = totalPageWithoutIsTotalRow.length
        const maxPage = Math.ceil(val.length / this.page.size)
        if (this.page.current > maxPage) {
          this.page.current = 1
        }
        this.requestLayoutUpdate()
      },
      immediate: true
    },
    pageSizeComputed: {
      handler: function (val) {
        this.page.size = val
      },
      immediate: true
    },
    "page.current": {
      handler: function (val) {
        if (this.pageCurrent) {
          this.$emit("update:pageCurrent", val)
        }
      },
      immediate: true
    },
    appliedMaxHeight: {
      handler() {
        this.requestLayoutUpdate()
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.setupHeaderObserver()
      this.requestLayoutUpdate()
    })
  },
  updated() {
    this.$nextTick(() => {
      this.setupHeaderObserver()
      this.requestLayoutUpdate()
    })
  },
  beforeUnmount() {
    this.teardownHeaderObserver()
    this.cancelScheduledLayout()
  },
  created() {
    let {page} = this.$route.query
    if (this.pageCurrent && this.pageCurrent > 0) {
      page = this.pageCurrent
    }
    if (page >= 0) {
      this.page.current = parseInt(page)
    }
    this.$router.replace({query: {}}).catch(err => {})
  },
  methods: {
    // paginate,
    clearSelection() {
      this.$refs["base-table"].clearSelection()
    },
    setupHeaderObserver() {
      if (typeof window === "undefined" || typeof window.ResizeObserver === "undefined") {
        return
      }
      const tableRef = this.$refs["base-table"]
      const headerEl = tableRef?.$refs?.headerWrapper
      if (!headerEl) {
        this.teardownHeaderObserver()
        return
      }
      if (this.observedHeaderEl === headerEl && this.headerResizeObserver) {
        return
      }
      this.teardownHeaderObserver()
      this.headerResizeObserver = new window.ResizeObserver((entries) => {
        const entry = entries?.[0]
        const height = entry?.contentRect?.height ?? headerEl?.offsetHeight ?? 0
        this.updateHeaderDelta(height)
        this.requestLayoutUpdate()
      })
      this.updateHeaderDelta(headerEl?.offsetHeight ?? 0)
      this.headerResizeObserver.observe(headerEl)
      this.observedHeaderEl = headerEl
    },
    updateHeaderDelta(currentHeight) {
      if (typeof currentHeight !== "number" || !currentHeight) {
        return
      }
      if (this.initialHeaderHeight === null) {
        this.initialHeaderHeight = currentHeight
        this.headerHeightDelta = 0
        return
      }
      const delta = currentHeight - this.initialHeaderHeight
      if (Math.abs(delta - this.headerHeightDelta) > 0.5) {
        this.headerHeightDelta = delta
      }
    },
    teardownHeaderObserver() {
      if (this.headerResizeObserver) {
        this.headerResizeObserver.disconnect()
        this.headerResizeObserver = null
      }
      this.observedHeaderEl = null
      this.initialHeaderHeight = null
      this.headerHeightDelta = 0
    },
    cancelScheduledLayout() {
      if (this.layoutRaf && typeof window !== "undefined" && typeof window.cancelAnimationFrame === "function") {
        window.cancelAnimationFrame(this.layoutRaf)
      }
      this.layoutRaf = null
    },
    requestLayoutUpdate() {
      const tableRef = this.$refs["base-table"]
      if (!tableRef || typeof tableRef.doLayout !== "function") {
        return
      }
      if (typeof window === "undefined" || typeof window.requestAnimationFrame !== "function") {
        tableRef.doLayout()
        return
      }
      this.cancelScheduledLayout()
      this.layoutRaf = window.requestAnimationFrame(() => {
        this.layoutRaf = null
        tableRef.doLayout()
      })
    }
  }
}
</script>
